import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, BadgeCheck, CheckCircle2, XCircle } from "lucide-react";
import { assocRegister, validateRef, formatApiError } from "@/lib/api";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Sections5";

const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-saffron";
const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";

const ROLE_INFO = {
    explorer: { name: "Ghumne Chaley Explorer", note: "For our existing customers who want to share trips they loved." },
    insider: { name: "Ghumne Chaley Travel Insider", note: "For travel planners and creators who also want to submit package ideas." },
};

export default function PartnerRegisterPage() {
    const [params] = useSearchParams();
    const role = (params.get("role") || "explorer").toLowerCase();
    const valid = role in ROLE_INFO;
    const [form, setForm] = useState({ name: "", mobile: "", email: "", password: "", experience: "", city: "", reference: "", referral_code: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [refState, setRefState] = useState({ status: "idle", name: null }); // idle | checking | valid | invalid
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const checkReferral = async (code) => {
        const c = code.trim().toUpperCase();
        if (!c) { setRefState({ status: "idle", name: null }); return; }
        if (!/^GC[EIAP]-[A-Z2-9]{6}$/.test(c)) { setRefState({ status: "invalid", name: null }); return; }
        setRefState({ status: "checking", name: null });
        try {
            const r = await validateRef(c);
            setRefState(r.valid ? { status: "valid", name: r.name } : { status: "invalid", name: null });
        } catch { setRefState({ status: "invalid", name: null }); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || form.mobile.replace(/\D/g, "").length < 10) {
            toast.error("Please fill name, email and a valid mobile number");
            return;
        }
        if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        setBusy(true);
        try {
            await assocRegister({ ...form, role });
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    if (!valid) {
        return (
            <div className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="register-invalid-role">
                <div className="max-w-md rounded-2xl bg-white border border-stone-200 p-8 text-center">
                    <h1 className="font-display font-black text-xl text-navy">By invitation only</h1>
                    <p className="mt-3 text-sm text-stone-600">Travel Ambassador and Privilege Member accounts are created personally by the Ghumne Chaley team. Email us and we'll take it forward.</p>
                    <div className="mt-5 flex flex-col gap-2">
                        <a href={`mailto:ghumnechaleyholidays@gmail.com?subject=${encodeURIComponent("Associate onboarding request")}`} data-testid="invite-email-cta"
                            className="rounded-full bg-navy px-5 py-2.5 text-xs font-black uppercase tracking-wider text-white">Email ghumnechaleyholidays@gmail.com</a>
                        <Link to="/partners" className="text-xs font-bold text-stone-500 hover:text-navy">Back to roles</Link>
                    </div>
                </div>
            </div>
        );
    }

    if (done) {
        return (
            <div className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="register-success">
                <div className="max-w-md rounded-2xl bg-white border border-green-200 p-8 text-center">
                    <BadgeCheck className="h-12 w-12 text-green-600 mx-auto" />
                    <h1 className="mt-3 font-display font-black text-xl text-navy">Application received!</h1>
                    <p className="mt-3 text-sm text-stone-600">Our team will review your {ROLE_INFO[role].name} application. Once approved, you'll get an email with your unique Associate ID, referral code and your community WhatsApp group invite.</p>
                    <Link to="/partners" className="mt-5 inline-block rounded-full bg-navy px-5 py-2.5 text-xs font-black uppercase tracking-wider text-white" data-testid="register-success-home">Back to Partners</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cream" data-testid="partner-register-page">
            <Header />
            <div className="py-12 px-6">
            <div className="max-w-lg mx-auto rounded-2xl bg-white border border-stone-200 p-7 shadow-sm">
                <p className="text-[11px] font-black uppercase tracking-wider text-saffron">{ROLE_INFO[role].name}</p>
                <h1 className="mt-1 font-display font-black text-2xl text-navy">Create your account</h1>
                <p className="mt-1 text-xs text-stone-500">{ROLE_INFO[role].note} Our team reviews every application before activation.</p>

                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="partner-register-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div><label className={lab}>Full name *</label>
                            <input className={inp} value={form.name} onChange={set("name")} data-testid="reg-name" placeholder="Your name" /></div>
                        <div><label className={lab}>Mobile (WhatsApp) *</label>
                            <input className={inp} value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))} data-testid="reg-mobile" placeholder="10-digit mobile" inputMode="numeric" /></div>
                    </div>
                    <div><label className={lab}>Email *</label>
                        <input className={inp} type="email" value={form.email} onChange={set("email")} data-testid="reg-email" placeholder="you@example.com" /></div>
                    <div><label className={lab}>Password * (min 8 characters)</label>
                        <input className={inp} type="password" value={form.password} onChange={set("password")} data-testid="reg-password" placeholder="Choose a password" /></div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div><label className={lab}>City</label>
                            <input className={inp} value={form.city} onChange={set("city")} data-testid="reg-city" placeholder="Mumbai" /></div>
                        <div><label className={lab}>How did you hear about us?</label>
                            <input className={inp} value={form.reference} onChange={set("reference")} data-testid="reg-reference" placeholder="Friend, trip, Instagram…" /></div>
                    </div>
                    <div><label className={lab}>Relevant experience</label>
                        <textarea rows={3} className={inp} value={form.experience} onChange={set("experience")} data-testid="reg-experience"
                            placeholder={role === "insider" ? "Trips planned, routes you know, groups you've handled…" : "Trips you've taken with us or your travel circle…"} /></div>
                    <div><label className={lab}>Referral Code <span className="normal-case font-normal text-stone-400">(optional)</span></label>
                        <div className="relative">
                            <input className={`${inp} uppercase pr-9`} value={form.referral_code} data-testid="reg-referral-code" placeholder="e.g. GCE-ABC123"
                                onChange={(e) => { setForm((f) => ({ ...f, referral_code: e.target.value })); checkReferral(e.target.value); }} />
                            <span className="absolute right-3 top-2.5">
                                {refState.status === "checking" && <Loader2 className="h-4 w-4 animate-spin text-stone-400" />}
                                {refState.status === "valid" && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                                {refState.status === "invalid" && <XCircle className="h-4 w-4 text-red-500" />}
                            </span>
                        </div>
                        {refState.status === "valid" && <p className="mt-1 text-[11px] font-bold text-green-700" data-testid="referral-valid">Referred by {refState.name}</p>}
                        {refState.status === "invalid" && form.referral_code.trim() && <p className="mt-1 text-[11px] font-bold text-red-600" data-testid="referral-invalid">That code doesn't match any associate</p>}
                    </div>
                    <button type="submit" disabled={busy} data-testid="reg-submit"
                        className="w-full rounded-full bg-saffron py-3.5 text-sm font-display font-black uppercase tracking-wider text-white hover:bg-saffron-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit application
                    </button>
                    <p className="text-center text-xs text-stone-500">Already a partner? <Link to="/partner/login" className="font-bold text-navy hover:text-saffron">Log in</Link></p>
                </form>
            </div>
            </div>
            <Footer />
        </div>
    );
}
